/**
 * Parses a `run_python` call's input and result for `RunPythonCard` and the pill. The result is the
 * payload `RunPythonTool.__shape_response` builds server-side; its `logs` are one
 * `[{offset:>8.3f}s] {message}` line per record (`log_extractor.py`).
 */

import { isPlainObject } from "./isPlainObject";
import { type ArtifactOutput, extractOutputsField } from "./outputsField";

export interface RunPythonPayload {
  /** The sandbox's `SandboxInvocationStatus`; a plain string so new statuses still parse. */
  status: string;
  /** `null` when the runner never answered, so no exit status was observed. */
  exitCode: number | null;
  runtimeS: number | null;
  logs: string;
  logsTruncated: boolean;
  outputs: ArtifactOutput[];
  /** The server's explanatory note for the model, if any. */
  note: string | null;
}

/**
 * Read a `run_python` result payload, or null when `output` is not one (the caller falls back to
 * JSON). Unwraps one `{json: ...}` layer, the shape of a non-text tool-result content block.
 */
export function detectRunPythonPayload(
  output: unknown,
): RunPythonPayload | null {
  if (!isPlainObject(output)) {
    return null;
  }
  const payload = isPlainObject(output.json) ? output.json : output;
  const { status, exit_code, runtime_s, logs, logs_truncated, note } = payload;
  if (typeof status !== "string" || typeof logs !== "string") {
    return null;
  }
  if (exit_code !== null && typeof exit_code !== "number") {
    return null;
  }
  return {
    status,
    exitCode: exit_code,
    runtimeS: typeof runtime_s === "number" ? runtime_s : null,
    logs,
    logsTruncated: logs_truncated === true,
    outputs: extractOutputsField(payload)?.artifacts ?? [],
    note: typeof note === "string" && note.length > 0 ? note : null,
  };
}

/**
 * How a finished `run_python` call ended. Derived from the payload status, not the tool's success
 * flag, which can't tell "your code failed" from "the sandbox failed".
 */
export type RunPythonOutcomeKind =
  | "completed"
  | "failed"
  | "timed_out"
  | "stopped"
  | "sandbox_error"
  | "unknown";

export interface RunPythonOutcome {
  kind: RunPythonOutcomeKind;
  /** The card's badge: "Completed", "Failed", "Timed out after 120 s". */
  label: string;
}

export interface RunPythonDuration {
  /** What the card and the pill show: the code's runtime, or the call's total time without one. */
  seconds: number;
  /** `code` when `seconds` is the code's own runtime; `total` when the sandbox reported none. */
  kind: "code" | "total";
  /** Total time minus the code's runtime (sandbox start/stop); null without a code runtime. */
  overheadSeconds: number | null;
}

export interface RunPythonCallSummary {
  outcome: RunPythonOutcome;
  duration: RunPythonDuration;
}

interface SummarizeRunPythonCallArgs {
  /** The parsed result, or null when the tool failed before producing a run payload. */
  payload: Pick<RunPythonPayload, "status" | "exitCode" | "runtimeS"> | null;
  /** Whether the tool result reports success; also false for a non-zero exit. */
  toolSucceeded: boolean;
  /** The tool result's wall-clock time, sandbox start-up and tear-down included. */
  runtimeMs: number;
  /** The call's `timeout_s` argument, when it set one. */
  timeoutS: number | null;
}

/** Statuses whose `runtime_s` measures only the code; otherwise it includes sandbox start-up. */
const CODE_RUNTIME_STATUSES: ReadonlySet<string> = new Set([
  "completed",
  "timed_out",
]);

/**
 * A finished call's duration for the card and the pill: the code's runtime when the payload reports
 * it, else the tool's total time.
 */
export function measureRunPythonDuration({
  payload,
  runtimeMs,
}: Pick<
  SummarizeRunPythonCallArgs,
  "payload" | "runtimeMs"
>): RunPythonDuration {
  const totalSeconds = runtimeMs / 1000;
  const codeSeconds =
    payload !== null &&
    payload.runtimeS !== null &&
    CODE_RUNTIME_STATUSES.has(payload.status)
      ? payload.runtimeS
      : null;
  return codeSeconds === null
    ? { seconds: totalSeconds, kind: "total", overheadSeconds: null }
    : {
        seconds: codeSeconds,
        kind: "code",
        overheadSeconds: Math.max(0, totalSeconds - codeSeconds),
      };
}

/**
 * One status and one duration ({@link measureRunPythonDuration}) for a finished `run_python` call.
 * A clean exit the tool still reports as failed, or a missing payload, is a sandbox error.
 */
export function summarizeRunPythonCall({
  payload,
  toolSucceeded,
  runtimeMs,
  timeoutS,
}: SummarizeRunPythonCallArgs): RunPythonCallSummary {
  return {
    outcome: describeOutcome(payload, toolSucceeded, timeoutS),
    duration: measureRunPythonDuration({ payload, runtimeMs }),
  };
}

function describeOutcome(
  payload: SummarizeRunPythonCallArgs["payload"],
  toolSucceeded: boolean,
  timeoutS: number | null,
): RunPythonOutcome {
  const sandboxError: RunPythonOutcome = {
    kind: "sandbox_error",
    label: "Sandbox error",
  };
  if (payload === null) {
    return sandboxError;
  }
  switch (payload.status) {
    case "completed": {
      if (payload.exitCode !== 0) {
        return { kind: "failed", label: "Failed" };
      }
      // Clean exit but tool failed: the sandbox errored after the code finished.
      return toolSucceeded
        ? { kind: "completed", label: "Completed" }
        : sandboxError;
    }
    case "timed_out": {
      const limit = timeoutS ?? payload.runtimeS;
      return {
        kind: "timed_out",
        label:
          limit === null
            ? "Timed out"
            : `Timed out after ${formatLimit(limit)}`,
      };
    }
    case "cancelled":
      return { kind: "stopped", label: "Stopped" };
    case "infra_error":
      return sandboxError;
    default:
      return {
        kind: "unknown",
        label: payload.status,
      };
  }
}

/** A duration for the card and the pill: "450 ms", "14.3 s", "2 m 05 s". */
export function formatSeconds(seconds: number): string {
  if (seconds < 1) {
    return `${String(Math.round(seconds * 1000))} ms`;
  }
  if (seconds < 60) {
    return `${(Math.round(seconds * 10) / 10).toFixed(1)} s`;
  }
  const whole = Math.round(seconds);
  return `${String(Math.floor(whole / 60))} m ${String(whole % 60).padStart(2, "0")} s`;
}

/** A time limit as the call set it: whole seconds, "120 s". */
export function formatLimit(seconds: number): string {
  return `${String(Math.round(seconds))} s`;
}

/** An elapsed time as a ticking clock reads: "0:07", "12:30". */
export function formatClock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(whole / 60))}:${String(whole % 60).padStart(2, "0")}`;
}

/** The badge's tooltip for a script that exited: "exit code 1". Null when no exit was observed. */
export function describeRunPythonExit(exitCode: number | null): string | null {
  return exitCode === null ? null : `exit code ${String(exitCode)}`;
}

/** The header duration's text: the code's runtime, or the call's total marked as such. */
export function formatRunPythonDuration(duration: RunPythonDuration): string {
  const seconds = formatSeconds(duration.seconds);
  return duration.kind === "total" ? `${seconds} total` : seconds;
}

/** The duration's tooltip: what the shown time covers and what the rest went on. */
export function describeRunPythonDuration(duration: RunPythonDuration): string {
  if (duration.kind === "total") {
    return `${formatSeconds(duration.seconds)} in total, sandbox included: the sandbox reported no runtime for the code`;
  }
  const code = `${formatSeconds(duration.seconds)} running your code`;
  return duration.overheadSeconds === null
    ? code
    : `${code} · ${formatSeconds(duration.overheadSeconds)} starting and stopping the sandbox`;
}

/**
 * Where an unfinished call stands, from the thread activity the executor sets; a running call with
 * no activity counts as running its code.
 */
export type RunPythonLivePhase =
  | { kind: "pending" }
  | { kind: "starting" }
  | { kind: "running" }
  | { kind: "other"; label: string };

export const STARTING_SANDBOX_ACTIVITY = "Starting the Python sandbox";
export const RUNNING_CODE_ACTIVITY = "Running the code";

export function describeRunPythonLivePhase(
  status: "pending" | "running",
  activity: string | null | undefined,
): RunPythonLivePhase {
  if (status === "pending") {
    return { kind: "pending" };
  }
  if (activity === STARTING_SANDBOX_ACTIVITY) {
    return { kind: "starting" };
  }
  if (!activity || activity === RUNNING_CODE_ACTIVITY) {
    return { kind: "running" };
  }
  return { kind: "other", label: activity };
}

/** The arguments the card's header line names. */
export interface RunPythonArguments {
  timeoutS: number | null;
}

/** Read the `timeout_s` a `run_python` call was given, ignoring a malformed value. */
export function extractRunPythonArguments(input: unknown): RunPythonArguments {
  if (!isPlainObject(input)) {
    return { timeoutS: null };
  }
  const { timeout_s } = input;
  return {
    timeoutS:
      typeof timeout_s === "number" && Number.isFinite(timeout_s)
        ? timeout_s
        : null,
  };
}

/**
 * A log line's kind, for styling: `traceback` (header, chaining sentences, quoted source), `frame`
 * (`File "...", line N`), `marker` (`^`/`~` underline), `error` (the final exception line).
 */
export type LogLineKind = "plain" | "traceback" | "frame" | "marker" | "error";

export interface LogLine {
  /** The run offset, e.g. `"0.012s"`, or null for a line without the executor's prefix. */
  offset: string | null;
  /** Whether to draw the offset: only when it differs from the line above. */
  showOffset: boolean;
  /** The message, with its own indentation intact. */
  text: string;
  kind: LogLineKind;
}

// Strips one space after the prefix; the message keeps its own leading whitespace.
const OFFSET_PREFIX = /^\[\s*(-?\d+(?:\.\d+)?s)\] ?/;
const TRACEBACK_HEADER = /^Traceback \(most recent call last\):/;
const CHAIN_SENTENCE =
  /^(During handling of the above exception|The above exception was the direct cause)/;
const FRAME_LINE = /^\s+File "/;
const MARKER_LINE = /^\s*[~^]+[~^\s]*$/;

/** Split offset-rendered logs into lines, separating offsets and classifying traceback lines. */
export function parseRunPythonLogs(logs: string): LogLine[] {
  if (logs.length === 0) {
    return [];
  }
  const rawLines = logs.split("\n");
  if (rawLines[rawLines.length - 1] === "") {
    rawLines.pop();
  }

  const lines: LogLine[] = [];
  let previousOffset: string | null = null;
  let inTraceback = false;
  for (const raw of rawLines) {
    const match = OFFSET_PREFIX.exec(raw);
    const offset = match ? match[1] : null;
    const text = match ? raw.slice(match[0].length) : raw;

    let kind: LogLineKind = "plain";
    if (TRACEBACK_HEADER.test(text)) {
      inTraceback = true;
      kind = "traceback";
    } else if (CHAIN_SENTENCE.test(text)) {
      kind = "traceback";
    } else if (inTraceback) {
      if (FRAME_LINE.test(text)) {
        kind = "frame";
      } else if (MARKER_LINE.test(text)) {
        kind = "marker";
      } else if (/^\s/.test(text)) {
        kind = "traceback";
      } else {
        // The first unindented line ends the traceback: it names the exception.
        kind = "error";
        inTraceback = false;
      }
    }

    lines.push({
      offset,
      showOffset: offset !== null && offset !== previousOffset,
      text,
      kind,
    });
    if (offset !== null) {
      previousOffset = offset;
    }
  }
  return lines;
}

/** The logs without the executor's offset prefixes, for copying. */
export function stripLogOffsets(logs: string): string {
  return logs
    .split("\n")
    .map((line) => line.replace(OFFSET_PREFIX, ""))
    .join("\n");
}

/**
 * The call's `code` for display, without the leading blank lines models often add. Null when there
 * is no usable code.
 */
export function extractRunPythonCode(input: unknown): string | null {
  if (!isPlainObject(input) || typeof input.code !== "string") {
    return null;
  }
  const code = input.code.replace(/^(\s*\n)+/, "").trimEnd();
  return code.length > 0 ? code : null;
}
